import { FC, useEffect, useState, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import CampaignContext from 'src/contexts/campaign/CampaignContext';
import { getCampaign } from 'src/services/campaign';
import { cleanObject_id } from 'src/lib/utils';
import { toastError } from 'src/services/toast';
import { ICampaign } from 'src/lib/schemas';
import { unstable_batchedUpdates } from 'react-dom';
import { isEqual } from 'lodash';

interface CampaignContextProps {
	children: React.ReactNode;
}
interface Step {
	label: string;
	isCompleted: boolean;
	isActive: boolean;
}
const initialSteps: Step[] = [
	{ label: 'Gather Information', isCompleted: false, isActive: true },
	{ label: 'Generate Designs', isCompleted: false, isActive: false },
	{ label: 'Publish', isCompleted: false, isActive: false },
];

// Fields that should be ignored when comparing campaigns for updates
const CAMPAIGN_COMPARISON_OMIT = ['updatedAt', 'lastUpdatedBy', 'createdAt'];

const CampaignProvider: FC<CampaignContextProps> = ({ children }) => {
	const [isFetching, setIsFetching] = useState<boolean>(true);
	const [campaign, setCampaign] = useState<ICampaign | null>(null);
	const [isEditorMode, setIsEditorMode] = useState<boolean>(false);

	// Add request deduplication to prevent multiple simultaneous requests
	const [isRequestInProgress, setIsRequestInProgress] =
		useState<boolean>(false);
	const requestRef = useRef<string | null>(null);

	const setCampaignState = (newCampaign: ICampaign | null) => {
		// Deep comparison to check if campaign actually changed
		const hasChanged = (() => {
			// If one is null and the other isn't, they're different
			if (!campaign && newCampaign) return true;
			if (campaign && !newCampaign) return true;
			if (!campaign && !newCampaign) return false;

			// If IDs are different, they're different campaigns
			if (campaign?.id !== newCampaign?.id) return true;

			// Deep comparison of relevant fields (excluding timestamps and metadata)
			const oldCampaignCleaned = campaign
				? Object.fromEntries(
						Object.entries(campaign).filter(
							([key]) => !CAMPAIGN_COMPARISON_OMIT.includes(key),
						),
					)
				: null;

			const newCampaignCleaned = newCampaign
				? Object.fromEntries(
						Object.entries(newCampaign).filter(
							([key]) => !CAMPAIGN_COMPARISON_OMIT.includes(key),
						),
					)
				: null;

			return !isEqual(oldCampaignCleaned, newCampaignCleaned);
		})();

		if (hasChanged) {
			setCampaign(newCampaign);
		}
	};
	const { campaignId: rawCampaignId } = useParams<{ campaignId: string }>();

	// Fallback to extract campaignId from URL if useParams fails
	const [campaignId, setCampaignId] = useState<string | undefined>(
		rawCampaignId,
	);

	useEffect(() => {
		const updateCampaignIdFromUrl = () => {
			// Extract campaignId from current URL as fallback
			const currentPath = window.location.pathname;
			const match = currentPath.match(/\/projects\/campaigns\/([^?/]+)/);
			const urlCampaignId = match ? match[1] : undefined;

			// If user navigated away from campaign pages, clear the context
			// Only check urlCampaignId because rawCampaignId from useParams can be stale
			if (!urlCampaignId) {
				if (campaignId) {
					setCampaignId(undefined);
					setCampaign(null);
				}
				return;
			}

			// Use URL extraction as primary, useParams as fallback
			const finalCampaignId = urlCampaignId || rawCampaignId;

			if (finalCampaignId !== campaignId) {
				// Special case: NEVER update campaignId from 'new' to real ID
				// This prevents remount and keeps the UX seamless
				const isTransitionFromNew =
					campaignId === 'new' && finalCampaignId !== 'new';

				if (isTransitionFromNew) {
					// NEVER update campaignId when transitioning from 'new' to real ID
					// Keep it as 'new' forever during the session to prevent remount
					// The autosave hook handles the real ID internally
					return;
				} else {
					setCampaignId(finalCampaignId);
				}
			}
		};

		// Initial check
		updateCampaignIdFromUrl();

		// Listen for URL changes (for navigate() calls)
		const handlePopState = () => {
			updateCampaignIdFromUrl();
		};

		window.addEventListener('popstate', handlePopState);

		// Also check periodically in case React Router doesn't trigger events
		const interval = setInterval(() => {
			// Only check URL if we're potentially in a campaign context
			const currentPath = window.location.pathname;
			const isCampaignPath = currentPath.startsWith('/projects/campaigns/');

			if (isCampaignPath || campaignId) {
				updateCampaignIdFromUrl();
			}
		}, 1000);

		return () => {
			window.removeEventListener('popstate', handlePopState);
			clearInterval(interval);
		};
	}, [rawCampaignId, campaignId]);
	const [isLaunching, setIsLaunching] = useState<boolean>(false);
	const [oldCampaignCopy, setOldCampaignCopy] = useState<ICampaign | null>(
		null,
	);
	const [wasCampaignChanged, setWasCampaignChanged] = useState(false);
	const [steps, setSteps] = useState<Step[]>(initialSteps);
	const [enabledSections, setEnabledSections] = useState<boolean[]>([
		true,
		false,
		false,
	]);
	const hasFinishedLaunchRef = useRef<boolean>(false);
	const isLaunchingRef = useRef(false);
	const sectionsInitialValue = !campaign?.designDirections?.length
		? [true, false, false, false]
		: [false, true, false, false];
	const [openSections, setOpenSections] = useState(sectionsInitialValue);

	const fetchCampaign = async (force: boolean = false) => {
		if (!campaignId || campaignId === 'new') {
			unstable_batchedUpdates(() => {
				setIsFetching(false);
				setCampaign(null);
			});
			return;
		}

		// Prevent duplicate requests for the same campaign (unless force is true)
		if (!force && isRequestInProgress && requestRef.current === campaignId) {
			return;
		}

		// Set request in progress
		unstable_batchedUpdates(() => {
			setIsRequestInProgress(true);
			setIsFetching(true);
		});
		requestRef.current = campaignId;

		try {
			const startTime = performance.now();
			const response = await getCampaign(campaignId);
			const fetchTime = performance.now() - startTime;

			if (!response) {
				throw new Error('No campaign data received from API');
			}

			const cleanedCampaign = cleanObject_id(response) as ICampaign;

			// Only update state if this is still the current campaign
			if (requestRef.current === campaignId) {
				unstable_batchedUpdates(() => {
					setCampaignState(cleanedCampaign);
					updateStepsAndSections(cleanedCampaign);
					setIsFetching(false);
					setIsRequestInProgress(false);
				});
			} else {
				unstable_batchedUpdates(() => {
					setIsFetching(false);
					setIsRequestInProgress(false);
				});
			}
		} catch (error: any) {
			console.error('🎯 CampaignProvider: Error fetching campaign', {
				campaignId,
				error: error.message,
				stack: error.stack,
				timestamp: new Date().toISOString(),
			});

			// Set campaign to null on error to prevent stale state
			if (requestRef.current === campaignId) {
				unstable_batchedUpdates(() => {
					setCampaignState(null);
					setIsFetching(false);
					setIsRequestInProgress(false);
				});
			}

			toastError(error);
		} finally {
			// Clear request in progress only if this was the current request
			if (requestRef.current === campaignId) {
				requestRef.current = null;
			}
		}
	};
	const resetStepsAndSections = () => {
		unstable_batchedUpdates(() => {
			setSteps(initialSteps);
			setEnabledSections([true, false, false]);
		});
	};
	const deepCopySteps = (steps: Step[]): Step[] => {
		return steps.map((step) => ({ ...step }));
	};

	const updateStepsAndSections = (campaign: ICampaign) => {
		const hasDesignDirections = campaign.designDirections?.length;
		const hasScheduledDate = campaign.schedule?.startDate;
		const isPublished = campaign.status === 'published';

		const newSteps = deepCopySteps(initialSteps);

		const newEnabledSections = [true, false, false, false];

		// Step 1: Gather Information
		if (hasDesignDirections) {
			if (newSteps[0]) newSteps[0].isCompleted = true;
			if (newSteps[1]) newSteps[1].isActive = true;
			newEnabledSections[1] = true;
			// Enable Step 3 for navigation but don't auto-complete Step 2
			newEnabledSections[2] = true;
		}

		// Step 2: Generate Designs - completed when has design directions
		// (already handled above)

		// Step 3: Publish - completed ONLY when published
		// Having a schedule date doesn't mean the campaign is ready to publish
		if (isPublished) {
			// If step 3 is completed, ensure all previous steps are also completed
			if (newSteps[0]) newSteps[0].isCompleted = true;
			if (newSteps[1]) newSteps[1].isCompleted = true;
			if (newSteps[2]) newSteps[2].isCompleted = true;
		}

		unstable_batchedUpdates(() => {
			setSteps(newSteps);
			setEnabledSections(newEnabledSections);
		});

		// Only save to localStorage if campaignId is valid
		if (campaignId && campaignId !== 'new') {
			try {
				localStorage.setItem(
					`enabledSections-${campaignId}`,
					JSON.stringify(newEnabledSections),
				);
			} catch (error) {
				console.error('Failed to save enabledSections to localStorage:', error);
			}
		}
	};
	const completeStep = (stepIndex: number) => {
		unstable_batchedUpdates(() => {
			setSteps((prevSteps) => {
				const updatedSteps = [...prevSteps];
				if (updatedSteps[stepIndex]) {
					updatedSteps[stepIndex].isCompleted = true;
					if (updatedSteps[stepIndex + 1]) {
						updatedSteps[stepIndex + 1].isActive = true;
					}
				}
				return updatedSteps;
			});
			setEnabledSections((prevSections) => {
				const updatedSections = [...prevSections];
				if (updatedSections[stepIndex + 1] === false) {
					updatedSections[stepIndex + 1] = true;
				}

				// Only save to localStorage if campaignId is valid
				if (campaignId && campaignId !== 'new') {
					try {
						localStorage.setItem(
							`enabledSections-${campaignId}`,
							JSON.stringify(updatedSections),
						);
					} catch (error) {
						console.error('Failed to save enabledSections to localStorage:', error);
					}
				}

				return updatedSections;
			});
		});
	};
	useEffect(() => {
		// Always reset steps and sections first
		unstable_batchedUpdates(() => {
			setSteps(
				initialSteps.map((step) => ({
					...step,
					isCompleted: false,
					isActive: false,
				})),
			);

			// Reset request state when campaignId changes
			setIsRequestInProgress(false);
			requestRef.current = null;

			// Reset campaign state when campaignId changes
			setCampaign(null);
		});

		// Only fetch if we have a valid campaignId
		if (campaignId && campaignId !== 'new') {
			fetchCampaign();
		} else {
			setIsFetching(false);
		}

		// Cleanup function to cancel any pending requests
		return () => {
			setIsRequestInProgress(false);
			requestRef.current = null;
		};
	}, [campaignId]); // fetchCampaign is not in dependencies, but it's a stable function

	const setOpenSectionsState = (
		newSections: boolean[] | ((prevSections: boolean[]) => boolean[]),
	) => {
		setOpenSections((prev) => {
			const newValue = typeof newSections === 'function' ? newSections(prev) : newSections;
			return newValue;
		});
	};

	// Public function to force refetch campaign data from the server
	// Useful when navigating from copilot to ensure populatedCustomerProfiles etc. are fresh
	const refetchCampaign = async () => {
		await fetchCampaign(true);
	};

	// Memoize the context value to prevent unnecessary re-renders
	const contextValue = useMemo(
		() => ({
			isFetching,
			id: campaignId,
			campaign,
			setCampaign: setCampaignState,
			refetchCampaign,
			isLaunching,
			setIsLaunching,
			steps,
			completeStep,
			enabledSections,
			openSections,
			setOpenSections: setOpenSectionsState,
			oldCampaignCopy,
			setOldCampaignCopy,
			wasCampaignChanged,
			setWasCampaignChanged,
			hasFinishedLaunchRef,
			isLaunchingRef,
			isEditorMode,
			setIsEditorMode,
		}),
		[
			isFetching,
			campaignId,
			campaign,
			isLaunching,
			steps,
			completeStep,
			enabledSections,
			openSections,
			oldCampaignCopy,
			wasCampaignChanged,
			hasFinishedLaunchRef,
			isLaunchingRef,
			isEditorMode,
		],
	);

	return (
		<CampaignContext.Provider value={contextValue}>
			{children}
		</CampaignContext.Provider>
	);
};
export default CampaignProvider;
